import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  FormControl,
  Stack,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import styled from "styled-components";
import OpaqueLoading from "../opaqueLoading/opaqueLoading";

const StyledDialogContent = styled(DialogContent)`
  padding: 16px;
`;

const ModalHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const SessionManagementModal = ({ onClose, fetchData, propData }) => {
  const [sessionName, setSessionName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  // Convert a full datetime string to yyyy-MM-dd for date inputs
  const formatForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Convert yyyy-MM-dd → MySQL DATETIME (YYYY-MM-DD HH:MM:SS)
  const formatForMySQL = (dateStr) => {
    if (!dateStr) return null;
    return `${dateStr} 00:00:00`;
  };

  useEffect(() => {
    if (propData) {
      setSessionName(propData?.sessionName || "");
      setStartDate(formatForInput(propData?.startDate));
      setEndDate(formatForInput(propData?.endDate));
    }
  }, [propData]);

  const submitHandler = async () => {
    if (!sessionName.trim() || !startDate.trim() || !endDate.trim()) {
      window.NotificationUtils.showError("Please fill all the details");
      return;
    }

    setLoading(true);
    try {
      const formattedStart = formatForMySQL(startDate);
      const formattedEnd = formatForMySQL(endDate);

      if (propData) {
        // Update existing session
        await window.Platform.database.updateSession({
          id: propData.id,
          sessionName,
          startDate: formattedStart,
          endDate: formattedEnd,
        });
        window.NotificationUtils.showSuccess("Session updated successfully");
      } else {
        // Add new session
        const result = await window.Platform.database.addSession({
          sessionName,
          startDate: formattedStart,
          endDate: formattedEnd,
        });
        window.NotificationUtils.showSuccess(result?.message || "Session added successfully");
      }

      setSessionName("");
      setStartDate("");
      setEndDate("");
      fetchData();
      onClose();
    } catch (err) {
      console.error(err);
      window.NotificationUtils.showError(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog disableEnforceFocus maxWidth="sm" fullWidth open>
      <StyledDialogContent>
        <ModalHeaderContainer>
          <Typography variant="h5">
            {propData ? "Edit Session" : "Add Session"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </ModalHeaderContainer>

        {loading && <OpaqueLoading />}

        <Stack gap={2} padding={2}>
          {/* Session Name */}
          <FormControl fullWidth>
            <TextField
              label="Session (e.g. 2025-2026)"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              fullWidth
            />
          </FormControl>

          {/* Start Date */}
          <FormControl fullWidth>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              // onChange={(e) => setStartDate(e.target.value)}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setStartDate(newStartDate);

                if (endDate && newStartDate && endDate < newStartDate) {
                  setEndDate("");
                }
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </FormControl>

          {/* End Date */}
          <FormControl fullWidth>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{
                min: startDate || undefined,
              }}
            />
          </FormControl>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              color="primary"
              variant="contained"
              onClick={submitHandler}
              disabled={!sessionName || !startDate || !endDate}
            >
              {propData ? "Update" : "Create"}
            </Button>
          </div>
        </Stack>
      </StyledDialogContent>
    </Dialog>
  );
};

export default SessionManagementModal;